/* Collection-only controller. Search retains its existing filter implementation. */
(() => {
  const panel = document.querySelector('[data-deferred-filters]');
  if (!panel) return;
  const content = panel.querySelector('[data-filter-content]');
  const status = panel.querySelector('[data-filter-status]');
  const message = panel.querySelector('[data-filter-message]');
  const retry = panel.querySelector('[data-filter-retry]');
  const soldOutParam = 'filter.p.m.custom.fully_sold_out';
  const availabilityParam = 'filter.v.availability';
  let pending;
  let loadedQuery;
  let loadedAt = 0;
  let retryAction;
  let sortItems = () => {};
  let warmupTimer;
  let idleTask;
  let warmupScheduled = false;
  let warmupCancelled = false;
  let warmupReady = false;

  function cancelWarmup() {
    warmupCancelled = true;
    clearTimeout(warmupTimer);
    if (idleTask !== undefined) {
      if (window.cancelIdleCallback) window.cancelIdleCallback(idleTask);
      else clearTimeout(idleTask);
    }
    window.removeEventListener('load', scheduleWarmup);
    document.removeEventListener('DOMContentLoaded', armWarmupFallback);
    document.removeEventListener('visibilitychange', resumeWarmup);
  }

  function resumeWarmup() {
    if (warmupReady) scheduleWarmup();
  }

  function scheduleWarmup() {
    warmupReady = true;
    if (warmupCancelled || warmupScheduled || document.visibilityState === 'hidden') return;
    warmupScheduled = true;
    clearTimeout(warmupTimer);
    const warm = () => {
      warmupScheduled = false;
      idleTask = undefined;
      if (warmupCancelled || document.visibilityState === 'hidden') return;
      // Read the current query at execution time; a shopper action cancels warmup.
      load(window.location.search.slice(1));
    };
    idleTask = window.requestIdleCallback
      ? window.requestIdleCallback(warm, { timeout: 2000 })
      : setTimeout(warm, 200);
  }

  function armWarmupFallback() {
    // Long-running app resources must not postpone preparing the drawer forever.
    warmupTimer = setTimeout(scheduleWarmup, 4000);
  }

  function sectionURL(query, sections, inStock = false) {
    const url = new URL(window.location.href);
    url.search = query;
    url.hash = '';
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    url.searchParams.delete('view');
    if (inStock) {
      url.searchParams.delete('page');
      url.searchParams.set('filter.v.availability', '1');
    }
    url.searchParams.set('sections', sections.join(','));
    return url;
  }

  function normalizeAvailability(query) {
    const params = new URLSearchParams(query);
    const native = params.getAll(availabilityParam);
    const soldOut = params.getAll(soldOutParam);
    if (soldOut.includes('1') || soldOut.includes('true') || (native.includes('0') && !native.includes('1'))) {
      params.delete(availabilityParam);
      params.set(soldOutParam, '1');
    } else {
      params.delete(soldOutParam);
      if (native.includes('0')) params.delete(availabilityParam);
      if (soldOut.includes('0') || soldOut.includes('false')) params.set(availabilityParam, '1');
    }
    return params.toString();
  }

  function readCounts(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const values = JSON.parse(parsed.querySelector('[data-stock-filter-counts]').textContent);
    const decoder = document.createElement('textarea');
    return new Map(values.map(value => {
      decoder.innerHTML = String(value.value).replace(/</g, '&lt;');
      return [JSON.stringify([value.name, decoder.value]), value.count];
    }));
  }

  async function fetchSections(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error('Filters unavailable');
    const sections = await response.json();
    for (const id of url.searchParams.get('sections').split(',')) {
      if (typeof sections[id] !== 'string') throw new Error('Missing filter section');
    }
    return sections;
  }

  function updateLayout() {
    const brandSearch = content.querySelector('#brand-filter-search');
    const term = (brandSearch?.value || '').toLowerCase();
    content.querySelectorAll('#brand-filters .facets__item').forEach(item => {
      item.classList.toggle('hidden', Number(item.dataset.stockCount) === 0 || !item.textContent.toLowerCase().includes(term));
    });
    content.querySelectorAll('.filter-group').forEach(group => {
      const visible = group.querySelectorAll('.facets__item:not(.hidden)').length;
      group.style.gridTemplateRows = `repeat(${Math.max(1, Math.ceil(visible / 2))}, auto)`;
      // Keep the brand search accessible when its search term has no matches.
      const hasStock = [...group.querySelectorAll('.facets__item')].some(item => Number(item.dataset.stockCount) > 0);
      group.closest('.filter-group-parent').classList.toggle('hidden', !hasStock);
      if (group.id === 'brand-filters') {
        const noBrands = content.querySelector('#no-brands-message');
        noBrands?.classList.toggle('hidden', visible > 0);
        noBrands?.setAttribute('aria-hidden', String(visible > 0));
      }
    });
  }

  function prepareFilters(html, countsHTML, query, availabilityHTML) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const disclosure = parsed.querySelector('.facets__disclosure');
    if (!disclosure) throw new Error('Missing filter form');
    const counts = countsHTML === null ? null : readCounts(countsHTML);
    const availabilityCounts = readCounts(availabilityHTML);
    const params = new URLSearchParams(query);
    disclosure.querySelectorAll('.facets__item').forEach(item => {
      const input = item.querySelector('input');
      const availability = input.name === availabilityParam || input.name === soldOutParam;
      if (input.name === availabilityParam && input.value === '0') {
        input.name = soldOutParam;
        input.value = '1';
      }
      if (availability) input.checked = params.getAll(input.name).includes(input.value);
      // In an explicit stock mode, use Shopify's counts for that exact mode.
      // Availability choices use a separate query without either stock restriction.
      const nativeCount = item.dataset.nativeCount;
      if (!counts && !/^\d+$/.test(nativeCount || '')) throw new Error('Missing native count');
      const key = JSON.stringify([input.name, input.value]);
      const count = availability
        ? (availabilityCounts.get(key) ?? 0)
        : counts ? (counts.get(key) ?? 0) : Number(nativeCount);
      if (!Number.isSafeInteger(count) || count < 0) throw new Error('Invalid stock count');
      item.dataset.stockCount = String(count);
      item.querySelector('.count').textContent = `(${count})`;
      item.classList.toggle('hidden', count === 0);
      input.disabled = count === 0 && !input.checked;
      const label = item.querySelector('span[aria-hidden="true"]').textContent;
      item.querySelector('.sr-only').textContent = `${label} (${count})`;
    });
    const sort = disclosure.querySelector('#hidden-sort-input');
    if (sort) sort.value = new URLSearchParams(query).get('sort_by') || document.querySelector('#sort-by')?.value || '';
    // Scripts returned by Section Rendering aren't executed; initialize explicitly.
    disclosure.querySelectorAll('script').forEach(script => script.remove());
    return disclosure;
  }

  function showBusy(keepCurrent) {
    panel.setAttribute('aria-busy', 'true');
    status.hidden = keepCurrent;
    status.setAttribute('data-loading', '');
    message.textContent = 'Loading filters…';
    message.classList.add('sr-only');
    retry.hidden = true;
    // Keep the last complete tree visible during a selection update. Prevent
    // another edit until the new tree and product results can replace it together.
    content.hidden = !keepCurrent;
    content.inert = keepCurrent;
  }

  async function load(query, updateGrid = false, updateHistory = false) {
    const normalized = normalizeAvailability(query);
    if (normalized !== query) {
      // Existing sold-out bookmarks must replace the variant-level results too.
      query = normalized;
      updateGrid = true;
      updateHistory = true;
    }
    cancelWarmup();
    pending?.abort();
    const controller = new AbortController();
    pending = controller;
    const focus = document.activeElement;
    const focusID = focus && content.contains(focus) ? focus.id : null;
    const scrollTop = content.querySelector('.facets__inner')?.scrollTop || 0;
    const openGroups = new Map([...content.querySelectorAll('details[data-index]')].map(el => [el.dataset.index, el.open]));
    const brandTerm = content.querySelector('#brand-filter-search')?.value || '';
    const gridID = document.getElementById('main-collection-product-grid').dataset.id;
    const sections = updateGrid ? [gridID, 'collection-filters'] : ['collection-filters'];
    retryAction = () => load(query, updateGrid, updateHistory);
    const keepCurrent = updateGrid && loadedQuery !== undefined && !!content.querySelector('.facets__disclosure');
    showBusy(keepCurrent);
    if (updateGrid) document.getElementById('CollectionProductGrid').setAttribute('aria-busy', 'true');
    try {
      const params = new URLSearchParams(query);
      const hasAvailability = params.has(availabilityParam) || params.has(soldOutParam);
      const unrestricted = new URLSearchParams(params);
      unrestricted.delete(availabilityParam);
      unrestricted.delete(soldOutParam);
      unrestricted.delete('page');
      if (!hasAvailability) sections.push('collection-filter-counts');
      const [view, counts] = await Promise.all([
        fetchSections(sectionURL(query, sections), controller.signal),
        fetchSections(hasAvailability
          ? sectionURL(unrestricted.toString(), ['collection-filter-counts'])
          : sectionURL(query, ['collection-filter-counts'], true), controller.signal)
      ]);
      if (controller.signal.aborted) return;
      const disclosure = prepareFilters(view['collection-filters'],
        hasAvailability ? null : counts['collection-filter-counts'], query,
        hasAvailability ? counts['collection-filter-counts'] : view['collection-filter-counts']);
      let grid;
      let count;
      let renderedSort;
      if (updateGrid) {
        const page = new DOMParser().parseFromString(view[gridID], 'text/html');
        grid = page.getElementById('CollectionProductGrid');
        count = page.querySelector('[data-product-count]');
        renderedSort = page.querySelector('#sort-by');
        if (!grid || !count) throw new Error('Missing product results');
      }
      content.replaceChildren(disclosure);
      content.querySelectorAll('.sort-num').forEach(sortItems);
      content.querySelectorAll('details[data-index]').forEach(el => {
        if (openGroups.has(el.dataset.index)) el.open = openGroups.get(el.dataset.index);
      });
      const brandSearch = content.querySelector('#brand-filter-search');
      if (brandSearch) brandSearch.value = brandTerm;
      updateLayout();
      if (updateGrid) {
        document.getElementById('CollectionProductGrid').innerHTML = grid.innerHTML;
        document.querySelectorAll('[data-product-count]').forEach(el => { el.innerHTML = count.innerHTML; });
        if (renderedSort) document.querySelector('#sort-by').value = renderedSort.value;
        document.getElementById('CollectionProductGrid').querySelectorAll('.scroll-trigger').forEach(el => el.classList.add('scroll-trigger--cancel'));
        if (typeof setupQuickbuys === 'function') setupQuickbuys();
      }
      if (updateHistory && query !== window.location.search.slice(1)) {
        history.pushState({ searchParams: query }, '', `${window.location.pathname}${query ? '?' + query : ''}`);
      }
      loadedQuery = query;
      loadedAt = Date.now();
      status.hidden = true;
      content.hidden = false;
      const scroller = content.querySelector('.facets__inner');
      if (scroller) scroller.scrollTop = scrollTop;
      document.dispatchEvent(new Event('ajaxFiltersUpdated'));
    } catch (error) {
      if (controller.signal.aborted) return;
      status.hidden = false;
      status.removeAttribute('data-loading');
      message.classList.remove('sr-only');
      message.textContent = keepCurrent
        ? 'Filters could not update. Previous results are still shown. Please try again.'
        : 'Filters could not load. Please try again.';
      retry.hidden = false;
      // On back/forward with a closed drawer, expose the recovery action too.
      if (updateGrid && !panel.classList.contains('panel-active')) window.openPopup('collectionFilters');
    } finally {
      if (pending === controller) {
        pending = null;
        content.inert = false;
        panel.removeAttribute('aria-busy');
        document.getElementById('CollectionProductGrid').removeAttribute('aria-busy');
        if (focusID && panel.classList.contains('panel-active') &&
            (document.activeElement === document.body || content.contains(document.activeElement))) {
          document.getElementById(focusID)?.focus({ preventScroll: true });
        }
      }
    }
  }

  content.addEventListener('input', event => {
    if (event.target.matches('.availability-facet') && event.target.checked) {
      content.querySelectorAll('.availability-facet').forEach(input => {
        if (input !== event.target) input.checked = false;
      });
    }
    if (event.target.id === 'brand-filter-search') {
      event.stopPropagation();
      updateLayout();
    }
  }, true);
  retry.addEventListener('click', () => retryAction?.());
  panel.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      window.closePopup(null, 'collectionFilters');
      document.querySelector('.open-filters-btn')?.focus();
    }
    if (event.key === 'Tab') {
      const focusable = [...panel.querySelectorAll('button, a[href], input, summary')].filter(el => !el.disabled && !el.closest('[inert]') && el.getClientRects().length);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first?.focus();
      }
    }
  });
  window.CrepslockerFilters = {
    init(sort) { sortItems = sort; },
    open() {
      panel.querySelector('.filter-header button')?.focus();
      const query = window.location.search.slice(1);
      if (loadedQuery === query && Date.now() - loadedAt < 30000 && !pending) return;
      if (!pending) load(query);
    },
    render(query, updateHistory) {
      const params = new URLSearchParams(query);
      // Preserve pagination when navigating back; reset it for a new filter choice.
      if (updateHistory) {
        params.delete('page');
        const currentSort = new URLSearchParams(window.location.search).get('sort_by');
        if (currentSort && !params.has('sort_by')) params.set('sort_by', currentSort);
      }
      return load(params.toString(), true, updateHistory);
    }
  };
  document.addEventListener('visibilitychange', resumeWarmup);
  if (document.readyState === 'complete') scheduleWarmup();
  else {
    window.addEventListener('load', scheduleWarmup, { once: true });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', armWarmupFallback, { once: true });
    else armWarmupFallback();
  }
})();
